import React, { useState, useEffect } from 'react';
import { Star, Trash2, User, Eye, Filter, Search } from 'lucide-react';
import AdminSidebar from '../../components/admin/AdminSidebar';
import DoctorFeedbackService from '../../services/doctor-feedback.service';
import { formatDate } from '../../utils/dateUtils';

const AdminFeedbackPage = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    loadAllFeedbacks();
  }, []);

  useEffect(() => {
    filterAndSortFeedbacks();
  }, [feedbacks, searchTerm, ratingFilter, sortBy]);

  const loadAllFeedbacks = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await DoctorFeedbackService.adminGetAllFeedbacks();
      setFeedbacks(response || []);
      
    } catch (err) {
      console.error('Errore nel caricamento dei feedback:', err);
      setError('Non è stato possibile caricare i feedback. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortFeedbacks = () => {
    let filtered = [...feedbacks];

    // Filtro per ricerca testuale
    if (searchTerm) {
      filtered = filtered.filter(feedback => 
        (feedback.therapist?.user?.first_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (feedback.therapist?.user?.last_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (feedback.patient?.user?.first_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (feedback.patient?.user?.last_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (feedback.comment || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro per rating
    if (ratingFilter !== 'all') {
      const targetRating = parseInt(ratingFilter);
      filtered = filtered.filter(feedback => feedback.rating === targetRating);
    }

    // Ordinamento
   filtered.sort((a, b) => {
  switch (sortBy) {
    case 'newest':
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    case 'oldest':
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    case 'rating_high':
      return b.rating - a.rating;
    case 'rating_low':
      return a.rating - b.rating;
    default:
      return 0;
  }
});

    setFilteredFeedbacks(filtered);
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo feedback? Questa azione non può essere annullata.')) {
      return;
    }

    try {
      await DoctorFeedbackService.adminDeleteFeedback(feedbackId);
      
      // Rimuovi il feedback dalla lista locale
      setFeedbacks(prev => prev.filter(feedback => feedback.id !== feedbackId));
      
      alert('Feedback eliminato con successo');
      
    } catch (err) {
      console.error('Errore nell\'eliminazione del feedback:', err);
      alert('Errore nell\'eliminazione del feedback. Riprova più tardi.');
    }
  };

  

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating
            ? 'text-yellow-400 fill-current'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  const getPatientName = (feedback) => {
    if (feedback.patient?.user) {
      const { first_name, last_name } = feedback.patient.user;
      return `${first_name} ${last_name}`;
    }
    return 'Paziente Anonimo';
  };

  const getTherapistName = (feedback) => {
    if (feedback.therapist?.user) {
      const { first_name, last_name } = feedback.therapist.user;
      return `Dr. ${first_name} ${last_name}`;
    }
    return `Terapeuta #${feedback.therapist_id}`;
  };

  const calculateStats = () => {
    if (feedbacks.length === 0) {
      return {
        totalFeedbacks: 0,
        averageRating: 0,
        ratingDistribution: [0, 0, 0, 0, 0]
      };
    }

    const totalRating = feedbacks.reduce((sum, feedback) => sum + feedback.rating, 0);
    const averageRating = totalRating / feedbacks.length;
    
    const ratingDistribution = [0, 0, 0, 0, 0];
    feedbacks.forEach(feedback => {
      ratingDistribution[feedback.rating - 1]++;
    });

    return {
      totalFeedbacks: feedbacks.length,
      averageRating: averageRating.toFixed(1),
      ratingDistribution
    };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <div className="w-64 bg-white shadow-md">
          <AdminSidebar />
        </div>
        <div className="flex-1 bg-gray-50 p-8 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-600 mb-4"></div>
            <p className="text-gray-600">Caricamento feedback...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <div className="w-64 bg-white shadow-md">
        <AdminSidebar />
      </div>

      <div className="flex-1 bg-gray-50 p-8">
        <header className="mb-8">
          <h1 className="text-2xl font-bold">Gestione Feedback</h1>
          <p className="text-gray-600">Monitora e gestisci tutti i feedback dei pazienti</p>
        </header>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-8">
            <p>{error}</p>
          </div>
        )}

        {/* Statistiche */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">Feedback Totali</p>
                <p className="text-2xl font-bold">{stats.totalFeedbacks}</p>
              </div>
              <Star className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">Valutazione Media</p>
                <p className="text-2xl font-bold">{stats.averageRating}/5</p>
              </div>
             <div className="flex">
              {/* @ts-ignore */}
              {renderStars(Math.round(parseFloat(stats.averageRating || 0)))}
            </div>
           </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div>
              <p className="text-gray-600 mb-2">Distribuzione Voti</p>
              <div className="space-y-1">
                {[5, 4, 3, 2, 1].map(rating => (
                  <div key={rating} className="flex items-center">
                    <span className="text-sm w-6">{rating}★</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2 mx-2">
                      <div 
                        className="bg-yellow-400 h-2 rounded-full" 
                        style={{ 
                          width: `${stats.totalFeedbacks > 0 ? (stats.ratingDistribution[rating - 1] / stats.totalFeedbacks) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600 w-8">
                      {stats.ratingDistribution[rating - 1]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Filtri */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <div className="flex items-center mb-4">
            <Filter className="w-5 h-5 text-gray-500 mr-2" />
            <h2 className="text-lg font-semibold">Filtri e Ricerca</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Ricerca */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ricerca
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Cerca per nome, dottore o commento..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              </div>
            </div>

            {/* Filtro Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Valutazione
              </label>
              <select
                value={ratingFilter}
                onChange={(e) => setRatingFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">Tutte le valutazioni</option>
                <option value="5">5 stelle</option>
                <option value="4">4 stelle</option>
                <option value="3">3 stelle</option>
                <option value="2">2 stelle</option>
                <option value="1">1 stella</option>
              </select>
            </div>

            {/* Ordinamento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ordinamento
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="newest">Più recenti</option>
                <option value="oldest">Più vecchi</option>
                <option value="rating_high">Valutazione alta</option>
                <option value="rating_low">Valutazione bassa</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lista Feedback */}
        <div className="bg-white rounded-lg shadow-md">
          {filteredFeedbacks.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {feedbacks.length === 0 ? 'Nessun feedback trovato' : 'Nessun risultato'}
              </h3>
              <p className="text-gray-500">
                {feedbacks.length === 0 
                  ? 'Non ci sono ancora feedback nel sistema.' 
                  : 'Prova a modificare i filtri di ricerca.'}
              </p>
            </div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold">
                  Feedback ({filteredFeedbacks.length})
                </h2>
              </div>
              
              {filteredFeedbacks.map((feedback) => (
                <div
                  key={feedback.id}
                  className="p-6 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-purple-600" />
                      </div>
                      
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {getPatientName(feedback)}
                        </h3>
                        <p className="text-sm text-gray-600">
                          per {getTherapistName(feedback)}
                        </p>
                        <div className="flex items-center mt-1 space-x-2">
                          <div className="flex">
                            {renderStars(feedback.rating)}
                          </div>
                          <span className="text-sm text-gray-500">
                            {formatDate(feedback.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center text-green-600">
                        <Eye className="w-4 h-4 mr-1" />
                        <span className="text-xs">Pubblico</span>
                      </div>
                      
                      <button
                        onClick={() => handleDeleteFeedback(feedback.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Elimina feedback"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {feedback.comment && (
                    <div className="ml-14">
                      <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                        &ldquo;{feedback.comment}&rdquo;
                      </p>
                    </div>
                  )}
                  
                  {!feedback.comment && (
                    <div className="ml-14">
                      <p className="text-gray-400 italic">
                        Nessun commento aggiunto
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminFeedbackPage;